// src/SitesList.js ver 00 Dec 14 2-25 12:55 pm - SB118
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './App';
import * as sitesService from './api/sitesService'; // Use new sitesService
import PaginationBar2 from './PaginationBar2'; // Assuming PaginationBar2 is in a separate file

function SitesList() {
    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const auth = useAuth();

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // Filter States
    const [categoryFilter, setCategoryFilter] = useState('no-filter');
    const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
    const categoryDropdownRef = useRef(null);

    const [statusFilter, setStatusFilter] = useState('Active'); // Default to 'Active'
    const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
    const statusDropdownRef = useRef(null);

    const TENANT_ID = auth.userProfile?.SubsCoDocId;

    // Allowed dropdown options for filters
    const allowedSiteCategories = ['HeadOffice', 'Warehouse', 'Central Kitchen', 'Restaurant', 'Kiosk'];
    const allowedSiteStatuses = ['Active', 'Not-Active'];


    useEffect(() => {
        const fetchSites = async () => {
            if (!TENANT_ID) {
                setError("No associated company found. Please create one.");
                setLoading(false);
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const token = await auth.getIdToken();
                if (!token) {
                    setError("Authentication required. Please log in.");
                    setLoading(false);
                    return;
                }

                // Pass all filter parameters to the service call
                const response = await sitesService.getSites(
                    TENANT_ID,
                    token,
                    currentPage,
                    itemsPerPage,
                    categoryFilter,
                    statusFilter
                );
                setSites(response.sites);
                setTotalItems(response.totalItems);
                setTotalPages(response.totalPages);
            } catch (err) {
                console.error("Error fetching sites from Cloud Function: ", err);
                setError(`Failed to load sites: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };
        fetchSites();
    }, [auth, TENANT_ID, currentPage, itemsPerPage, categoryFilter, statusFilter]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleLimitChange = (newLimit) => {
        setItemsPerPage(newLimit);
        setCurrentPage(1); // Reset to first page when limit changes
    };

    // Generic toggle for any dropdown
    const toggleDropdown = (setter) => {
        setter(prev => !prev);
    };

    // Filter change handlers
    const handleCategoryFilterChange = (filter) => {
        setCategoryFilter(filter);
        setCurrentPage(1);
        setIsCategoryDropdownOpen(false);
    };

    const handleStatusFilterChange = (filter) => {
        setStatusFilter(filter);
        setCurrentPage(1);
        setIsStatusDropdownOpen(false);
    };


    // Close all dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
                setIsCategoryDropdownOpen(false);
            }
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
                setIsStatusDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);


    if (loading) return <p>Loading sites...</p>;
    if (error) return <p style={{ color: 'red' }}>Error: {error}</p>;
    if (!TENANT_ID) return <p style={{ color: 'orange' }}>Please create a company profile to manage sites.</p>;


    return (
        <div>
            <div className="page-header">
                <h2>Sites</h2>
                {/* <Link to={`/sites/add/${TENANT_ID}`} className="btn secondary">Add New Site</Link> */}
                <Link to={`/sites/add`} className="btn secondary">Add New Site</Link>
            </div>

            <PaginationBar2
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
            />

            <table>
                <thead>
                    <tr>
                        <th
                            className="dropdown-th"
                            onClick={() => toggleDropdown(setIsCategoryDropdownOpen)}
                            ref={categoryDropdownRef}
                            title="Click to Filter"
                        >
                            Category {categoryFilter !== 'no-filter' && `(${categoryFilter})`}
                            {isCategoryDropdownOpen && (
                                <ul className="dropdown-menu">
                                    <li onClick={() => handleCategoryFilterChange('no-filter')}>&lt;no filter&gt;</li>
                                    {allowedSiteCategories.map(cat => (
                                        <li key={cat} onClick={() => handleCategoryFilterChange(cat)}>{cat}</li>
                                    ))}
                                </ul>
                            )}
                        </th>
                        <th>Name</th>
                        <th>Town/City</th>
                        <th>State</th>
                        <th>Country</th>
                        <th>Contact</th>
                        <th
                            className="dropdown-th"
                            onClick={() => toggleDropdown(setIsStatusDropdownOpen)}
                            ref={statusDropdownRef}
                            title="Click to Filter"
                        >
                            Status {statusFilter !== 'no-filter' && `(${statusFilter})`}
                            {isStatusDropdownOpen && (
                                <ul className="dropdown-menu">
                                    <li onClick={() => handleStatusFilterChange('no-filter')}>&lt;no filter&gt;</li>
                                    {allowedSiteStatuses.map(status => (
                                        <li key={status} onClick={() => handleStatusFilterChange(status)}>{status}</li>
                                    ))}
                                </ul>
                            )}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {sites.map(site => (
                        <tr key={site.id}>
                            <td>{site.siteCategory}</td>
                            <td>
                                <Link to={`/sites/details/${site.id}`}> {/* Link to show all details */}
                                    {site.siteName}
                                </Link>
                            </td>
                            <td>{site.TownOrCity}</td>
                            <td>{site.StateOrProvince}</td>
                            <td>{site.Country}</td>
                            <td>{site.siteContactFullName}</td>
                            <td>{site.Status}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <PaginationBar2
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
            />
        </div>
    );
}

export default SitesList;